import { tv } from "@ui/lib/tv";
import { cx } from "@ui/utils/cx";

/**
 * Shared chrome for a single passport badge slot, used by the profile strip
 * (`PassportBadges`, fixed size) and the full passport book (`PassportBook`,
 * fluid — sized by its grid cell).
 */
export const passportSlot = tv({
  slots: {
    slot: "relative shrink-0",
    placeholderChrome:
      "overflow-clip rounded-t-[69px] rounded-b-[10px] bg-white shadow-[0px_2.6px_8.6px_rgba(0,0,0,0.1),0px_0.9px_0px_rgba(0,0,0,0.08),0px_0px_0px_0.9px_rgba(0,0,0,0.05)]",
  },
  variants: {
    size: {
      fixed: { slot: "size-[68px] lg:size-[88px]" },
      fluid: { slot: "aspect-square w-full" },
    },
    // The strip stickers are playfully askew; the passport book overlays them on
    // upright dashed slots baked into the art, so it opts out of the tilt.
    rotation: {
      tilt: { slot: "-rotate-1" },
      none: { slot: "" },
    },
  },
  defaultVariants: {
    size: "fixed",
    rotation: "tilt",
  },
});

// The 249 country flags share one arch template (a 1360² square parked in its
// canvas). The achievement badges each ship a bespoke 250px thumb (varying aspect —
// see the in-app WebP under images/badges/platform), so each gets its own fit: the
// OPAQUE emblem (not the soft drop-shadow, which reaches the canvas edge) scaled to
// ~110% of the square slot and centred, so platform emblems read deliberately larger
// and more celebratory than the flags. Tiered badges key on their tier-suffixed
// image_key (e.g. countries_visited.5), since each tier is a distinct thumb. Values
// computed from each thumb's opaque-alpha bbox (capped at 122% img width, under the
// ~126% inter-slot pitch so neighbours never touch) — re-derive if art is resized.
const badgeFit = tv({
  base: "absolute max-w-none",
  variants: {
    template: {
      country: "-top-[1.48%] -left-[5.22%] w-[110%]",
      "celebration.first": "-top-[1.75%] -left-[6.5%] w-[113%]",
      "countries_visited.5": "-top-[6.5%] -left-[3.28%] w-[107.41%]",
      featured_family: "-top-[0.71%] -left-[7.89%] w-[115.78%]",
      founding_family: "-top-[7.45%] -left-[9.36%] w-[119.2%]",
      joined_up: "top-[7.15%] -left-[8.38%] w-[117.71%]",
      "product.first_listed": "top-[2.42%] -left-[11.49%] w-[122%]",
      "products_sold.1": "-top-[4.41%] -left-[11%] w-[122%]",
      "trip.first": "-top-[6.03%] -left-[8.87%] w-[118.7%]",
    },
  },
  defaultVariants: { template: "country" },
});

type BadgeTemplate =
  | "country"
  | "celebration.first"
  | "countries_visited.5"
  | "featured_family"
  | "founding_family"
  | "joined_up"
  | "product.first_listed"
  | "products_sold.1"
  | "trip.first";

// Achievement badges with their own tight-cropped thumb fit on their (tier-suffixed)
// image_key; everything else — the 249 country flags + any not-yet-arted tier —
// falls back to the shared country arch fit.
const PLATFORM_TEMPLATES = new Set<string>([
  "celebration.first",
  "countries_visited.5",
  "featured_family",
  "founding_family",
  "joined_up",
  "product.first_listed",
  "products_sold.1",
  "trip.first",
]);

function badgeTemplate(imageKey: string | null | undefined): BadgeTemplate {
  return imageKey && PLATFORM_TEMPLATES.has(imageKey) ? (imageKey as BadgeTemplate) : "country";
}

/** Dashed badge-shaped outline matching the art's empty slots. */
export function EmptyBadgePlaceholder() {
  return (
    <svg aria-hidden="true" className="size-full" fill="none" viewBox="7 4 92 92" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.62826 50.8019C8.20415 26.5051 27.5568 6.46479 51.8537 6.04069C76.1505 5.61659 96.1908 24.9693 96.6149 49.2661L97.2022 82.9161C97.3019 88.6285 92.7519 93.3402 87.0395 93.4399L19.7394 94.6147C14.027 94.7144 9.31533 90.1644 9.21562 84.4519L8.62826 50.8019Z"
        fill="white"
      />
      <path
        d="M57.295 13.162C60.6111 13.5357 63.7987 14.3432 66.7996 15.5267L66.563 16.1266C69.5816 17.3171 72.4077 18.8954 74.9789 20.8008L75.3625 20.2833C77.996 22.2349 80.3657 24.5234 82.4081 27.0871L81.9052 27.4885C83.8992 29.9916 85.5752 32.761 86.8702 35.7362L87.4596 35.4788C88.7472 38.4367 89.6662 41.5939 90.1556 44.8951L89.5175 44.9892C89.7506 46.5616 89.8855 48.1678 89.914 49.8004L89.981 53.6367L90.6274 53.6255L90.7613 61.2991L90.1149 61.3104L90.2489 68.984L90.8952 68.9727L91.0292 76.6464L90.3828 76.6577L90.4498 80.494C90.4644 81.3322 90.3111 82.1328 90.0223 82.8651L90.6223 83.1017C89.96 84.7813 88.6503 86.1375 86.9949 86.858L86.7374 86.2667C86.0157 86.5808 85.221 86.762 84.3827 86.7766L80.5464 86.8436L80.5577 87.49L72.884 87.6239L72.8727 86.9775L65.1991 87.1115L65.2104 87.7578L57.5367 87.8918L57.5255 87.2454L49.8518 87.3793L49.8631 88.0257L42.1905 88.1597L42.1792 87.5133L34.5055 87.6472L34.5168 88.2936L26.8432 88.4275L26.8319 87.7812L22.9956 87.8481C22.1572 87.8628 21.3559 87.7095 20.6235 87.4207L20.3869 88.0216C18.7073 87.3593 17.3511 86.0488 16.6306 84.3932L17.2229 84.1358C16.948 83.5042 16.7749 82.817 16.7262 82.0934L16.7129 81.7811L16.646 77.9447L15.9996 77.956L15.8657 70.2824L16.512 70.2711L16.3781 62.5975L15.7317 62.6087L15.5978 54.9351L16.2441 54.9238L16.1772 51.0875C16.1487 49.4549 16.2265 47.845 16.4046 46.2654L15.7636 46.1936C16.1374 42.8775 16.9448 39.6898 18.1283 36.6889L18.7283 36.9256C19.9187 33.9071 21.4971 31.0808 23.4024 28.5097L22.8849 28.1261C24.8365 25.4927 27.125 23.1228 29.6888 21.0805L30.0902 21.5843C32.5932 19.5904 35.3627 17.9144 38.3378 16.6193L38.0804 16.028C41.0381 14.7405 44.1957 13.8223 47.4967 13.333L47.5908 13.9711C49.1632 13.738 50.7694 13.604 52.4021 13.5755C54.0345 13.5471 55.6438 13.6249 57.2232 13.803L57.295 13.162Z"
        fill="white"
        stroke="#E0E0E0"
        strokeWidth="1.2931"
        strokeDasharray="8.62 8.62"
      />
    </svg>
  );
}

/** A badge PNG scaled to fill its (relative) slot, with bottom shadow overflow. */
export function BadgeImage({
  url,
  alt,
  imageKey,
  className,
}: {
  url: string;
  alt: string;
  imageKey?: string | null;
  className?: string;
}) {
  return (
    <img
      src={url}
      alt={alt}
      decoding="async"
      className={cx(badgeFit({ template: badgeTemplate(imageKey) }), className)}
    />
  );
}
